import * as XLSX from "xlsx";
import type { Node, Edge } from "@xyflow/react";
import { ResponseType, NodeData } from "../types/types";
import * as docx from "docx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { stripHtmlFromLabel } from "../utils/textFormatting";

export const handleExportToExcel = async (
  nodes: Node<NodeData>[],
  edges: Edge[],
): Promise<ResponseType> => {
  try {
    const data = nodes.map((node) => ({
      id: node.id,
      parent: node.parentId || "Root",
      label: stripHtmlFromLabel(node.data.label),
      icon: node.data.icon,
      object: node.data.object,
      objectType: node.data.objectType,
      objectWidth: node.data.objectWidth,
      objectHeight: node.data.objectHeight,
      notes: node.data.notes,
      complete: node.data.complete ? "Yes" : "No",
      status: node.data.status,
      flagged: node.data.flagged,
      color: node.data.color,
      backgroundColor: node.data.backgroundColor,
      presentationOrder: node.data.presentationOrder,
      priority: node.data.priority,
      links:
        node.data.links
          ?.map((link) => `${link.title}: ${link.url}`)
          .join("; ") || "",
      value: node.data.value,
      values:
        node.data.values?.map((v) => `${v.title}=${v.value}`).join("; ") || "",
      rollupValues:
        node.data.rollupValues
          ?.map((v) => `${v.title}=${v.value}`)
          .join("; ") || "",
      customProperties:
        node.data.customProperties
          ?.map((cp) => `${cp.name}:${cp.value}`)
          .join("; ") || "",
      eventTitle: node.data.event?.title || "",
      eventDescription: node.data.event?.description || "",
      eventDate: node.data.event?.date || "",
      addressName: node.data.address?.name || "",
      addressStreet: node.data.address?.street || "",
      addressCity: node.data.address?.city || "",
      addressZipCode: node.data.address?.zip_code || "",
      addressPhone: node.data.address?.Phone || "",
      addressEmail: node.data.address?.email || "",
      taskTitle: node.data.task?.title || "",
      taskStatus: node.data.task?.status || "",
      taskPriority: node.data.task?.priority || "",
      taskAssignee: node.data.task?.assignee || "",
      taskStartDate: node.data.task?.startDate || "",
      taskEndDate: node.data.task?.endDate || "",
      taskEstimate: node.data.task?.estimate || "",
      ms365DocumentTitle: node.data.ms365Document?.title || "",
      ms365DocumentType: node.data.ms365Document?.type || "",
      ms365DocumentUrl: node.data.ms365Document?.webUrl || "",
      ms365DocumentTimestamp: node.data.ms365Document?.timestamp || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Nodes");

    const edgesData = edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
    }));

    const edgesWorksheet = XLSX.utils.json_to_sheet(edgesData);
    XLSX.utils.book_append_sheet(workbook, edgesWorksheet, "Edges");

    XLSX.writeFile(workbook, "map_data.xlsx");
    return {
      success: true,
      description: "Data exported successfully.",
    };
  } catch (error) {
    return {
      success: false,
      description: `There was an error exporting the data to Excel: ${
        (error as Error).message
      }`,
    } as ResponseType;
  }
};

export const handleExportToWord = async (
  nodes: Node<NodeData>[],
  edges: Edge[],
  asTable: boolean = true,
  captureScreenshot?: () => Promise<string | null>,
): Promise<ResponseType> => {
  try {
    // Find center node
    const centerNode = nodes.find((n) => n.type === "center") || nodes[0];

    // Capture screenshot if function provided
    let screenshotDataUrl: string | null = null;
    if (captureScreenshot) {
      screenshotDataUrl = await captureScreenshot();
    }

    // Build hierarchy from center node outward
    const buildHierarchy = (
      nodeId: string,
      visited: Set<string> = new Set(),
    ): Node<NodeData>[] => {
      if (visited.has(nodeId)) return [];
      visited.add(nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return [];

      const children = nodes.filter((n) => n.parentId === nodeId);
      const result: Node<NodeData>[] = [node];

      children.forEach((child) => {
        result.push(...buildHierarchy(child.id, visited));
      });

      return result;
    };

    const orderedNodes = buildHierarchy(centerNode.id);

    // Process screenshot image early to prepare it
    let screenshotImageData: string | null = null;
    if (screenshotDataUrl) {
      try {
        // Extract base64 data from data URL
        const base64Data = screenshotDataUrl.split(",")[1];
        screenshotImageData = base64Data;
      } catch (error) {
        console.error("Error processing screenshot:", error);
      }
    }

    // Create detailed node descriptions
    const createNodeParagraphs = (node: Node<NodeData>): docx.Paragraph[] => {
      const paragraphs: docx.Paragraph[] = [];

      // Title
      paragraphs.push(
        new docx.Paragraph({
          text: stripHtmlFromLabel(node.data.label),
          heading: docx.HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 },
        }),
      );

      // Basic Information
      if (node.data.notes) {
        paragraphs.push(
          new docx.Paragraph({
            text: `Notes: ${node.data.notes}`,
            spacing: { after: 100 },
          }),
        );
      }

      if (node.data.status && node.data.status !== "none") {
        paragraphs.push(
          new docx.Paragraph({
            text: `Status: ${node.data.status}`,
          }),
        );
      }

      paragraphs.push(
        new docx.Paragraph({
          text: `Complete: ${node.data.complete ? "Yes" : "No"}`,
        }),
      );

      if (node.data.priority) {
        paragraphs.push(
          new docx.Paragraph({
            text: `Priority: ${node.data.priority}`,
          }),
        );
      }

      if (node.data.flagged && node.data.flagged !== "none") {
        paragraphs.push(
          new docx.Paragraph({
            text: `Flagged: ${node.data.flagged}`,
          }),
        );
      }

      // Links
      if (node.data.links && node.data.links.length > 0) {
        paragraphs.push(
          new docx.Paragraph({
            children: [new docx.TextRun({ text: "Links:", bold: true })],
            spacing: { before: 100 },
          }),
        );
        node.data.links.forEach((link) => {
          paragraphs.push(
            new docx.Paragraph({
              text: `  • ${link.title}: ${link.url}`,
            }),
          );
        });
      }

      // Values
      if (node.data.values && node.data.values.length > 0) {
        paragraphs.push(
          new docx.Paragraph({
            children: [new docx.TextRun({ text: "Values:", bold: true })],
            spacing: { before: 100 },
          }),
        );
        node.data.values.forEach((v) => {
          paragraphs.push(
            new docx.Paragraph({
              text: `  • ${v.title}: ${v.value}`,
            }),
          );
        });
      }

      // Custom Properties
      if (node.data.customProperties && node.data.customProperties.length > 0) {
        paragraphs.push(
          new docx.Paragraph({
            children: [
              new docx.TextRun({ text: "Custom Properties:", bold: true }),
            ],
            spacing: { before: 100 },
          }),
        );
        node.data.customProperties.forEach((cp) => {
          paragraphs.push(
            new docx.Paragraph({
              text: `  • ${cp.name}: ${cp.value}`,
            }),
          );
        });
      }

      // Event Information
      if (node.data.event?.title) {
        paragraphs.push(
          new docx.Paragraph({
            children: [new docx.TextRun({ text: "Event:", bold: true })],
            spacing: { before: 100 },
          }),
        );
        paragraphs.push(
          new docx.Paragraph({
            text: `  Title: ${node.data.event.title}`,
          }),
        );
        if (node.data.event.description) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Description: ${node.data.event.description}`,
            }),
          );
        }
        if (node.data.event.date) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Date: ${node.data.event.date}`,
            }),
          );
        }
      }

      // Address Information
      if (node.data.address?.name) {
        paragraphs.push(
          new docx.Paragraph({
            children: [new docx.TextRun({ text: "Address:", bold: true })],
            spacing: { before: 100 },
          }),
        );
        paragraphs.push(
          new docx.Paragraph({
            text: `  ${node.data.address.name}`,
          }),
        );
        if (node.data.address.street) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  ${node.data.address.street}`,
            }),
          );
        }
        if (node.data.address.city || node.data.address.zip_code) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  ${node.data.address.zip_code || ""} ${
                node.data.address.city || ""
              }`,
            }),
          );
        }
        if (node.data.address.Phone) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Phone: ${node.data.address.Phone}`,
            }),
          );
        }
        if (node.data.address.email) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Email: ${node.data.address.email}`,
            }),
          );
        }
      }

      // Task Information
      if (node.data.task?.title) {
        paragraphs.push(
          new docx.Paragraph({
            children: [new docx.TextRun({ text: "Task:", bold: true })],
            spacing: { before: 100 },
          }),
        );
        paragraphs.push(
          new docx.Paragraph({
            text: `  Title: ${node.data.task.title}`,
          }),
        );
        if (node.data.task.status) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Status: ${node.data.task.status}`,
            }),
          );
        }
        if (node.data.task.priority) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Priority: ${node.data.task.priority}`,
            }),
          );
        }
        if (node.data.task.assignee) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Assignee: ${node.data.task.assignee}`,
            }),
          );
        }
        if (node.data.task.startDate) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Start Date: ${node.data.task.startDate}`,
            }),
          );
        }
        if (node.data.task.endDate) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  End Date: ${node.data.task.endDate}`,
            }),
          );
        }
        if (node.data.task.estimate) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Estimate: ${node.data.task.estimate}`,
            }),
          );
        }
      }

      // MS365 Document
      if (node.data.ms365Document?.title) {
        paragraphs.push(
          new docx.Paragraph({
            children: [new docx.TextRun({ text: "Document:", bold: true })],
            spacing: { before: 100 },
          }),
        );
        paragraphs.push(
          new docx.Paragraph({
            text: `  Title: ${node.data.ms365Document.title}`,
          }),
        );
        if (node.data.ms365Document.type) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  Type: ${node.data.ms365Document.type}`,
            }),
          );
        }
        if (node.data.ms365Document.webUrl) {
          paragraphs.push(
            new docx.Paragraph({
              text: `  URL: ${node.data.ms365Document.webUrl}`,
            }),
          );
        }
      }

      // Separator line
      paragraphs.push(
        new docx.Paragraph({
          text: "─".repeat(5),
          spacing: { before: 200, after: 200 },
        }),
      );

      return paragraphs;
    };

    // Build document content
    const allParagraphs: docx.Paragraph[] = [
      new docx.Paragraph({
        text: stripHtmlFromLabel(centerNode.data.label),
        heading: docx.HeadingLevel.HEADING_1,
        spacing: { after: 400 },
      }),
    ];

    // Add screenshot if available
    if (screenshotImageData) {
      try {
        allParagraphs.push(
          new docx.Paragraph({
            children: [
              new docx.ImageRun({
                type: "png" as "png",
                data: screenshotImageData,
                transformation: {
                  width: 600,
                  height: 450,
                },
              }),
            ],
            spacing: { before: 200, after: 400 },
          }),
        );
      } catch (error) {
        console.error("Error adding screenshot to document:", error);
      }
    }

    orderedNodes.forEach((node) => {
      allParagraphs.push(...createNodeParagraphs(node));
    });

    const doc = new docx.Document({
      title: stripHtmlFromLabel(centerNode.data.label),
      description: "Data exported from the map.",
      creator: "FocusMap",
      sections: [
        {
          children: allParagraphs,
        },
      ],
    });

    const blob = await docx.Packer.toBlob(doc);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "map_data.docx";
    link.click();

    return {
      success: true,
      description: "Data exported successfully.",
    };
  } catch (error) {
    return {
      success: false,
      description: `There was an error exporting the data to Word: ${
        (error as Error).message
      }`,
    } as ResponseType;
  }
};

export const handleExportToPDF = async (
  nodes: Node<NodeData>[],
  edges: Edge[],
  captureScreenshot?: () => Promise<string | null>,
): Promise<ResponseType> => {
  try {
    // Find center node
    const centerNode = nodes.find((n) => n.type === "center") || nodes[0];

    // Capture screenshot if function provided
    let screenshotDataUrl: string | null = null;
    if (captureScreenshot) {
      screenshotDataUrl = await captureScreenshot();
    }

    // Build hierarchy from center node outward
    const buildHierarchy = (
      nodeId: string,
      visited: Set<string> = new Set(),
    ): Node<NodeData>[] => {
      if (visited.has(nodeId)) return [];
      visited.add(nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return [];

      const children = nodes.filter((n) => n.parentId === nodeId);
      const result: Node<NodeData>[] = [node];

      children.forEach((child) => {
        result.push(...buildHierarchy(child.id, visited));
      });

      return result;
    };

    const orderedNodes = buildHierarchy(centerNode.id);

    // Create PDF document
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Add title
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text(
      stripHtmlFromLabel(centerNode.data.label),
      pageWidth / 2,
      yPosition,
      {
        align: "center",
      },
    );
    yPosition += 15;

    // Add screenshot if available
    if (screenshotDataUrl) {
      try {
        const imgWidth = pageWidth - 2 * margin;
        const imgHeight = (imgWidth * 3) / 4; // Maintain aspect ratio

        // Check if we need a new page
        if (yPosition + imgHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }

        doc.addImage(
          screenshotDataUrl,
          "PNG",
          margin,
          yPosition,
          imgWidth,
          imgHeight,
        );
        yPosition += imgHeight + 10;
      } catch (error) {
        console.error("Error adding screenshot to PDF:", error);
      }
    }

    // Add nodes data
    orderedNodes.forEach((node) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 40) {
        doc.addPage();
        yPosition = margin;
      }

      // Node title
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(stripHtmlFromLabel(node.data.label), margin, yPosition);
      yPosition += 7;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      // Basic information
      if (node.data.notes) {
        const lines = doc.splitTextToSize(
          `Notes: ${node.data.notes}`,
          pageWidth - 2 * margin,
        );
        lines.forEach((line: string) => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += 5;
        });
      }

      if (node.data.status && node.data.status !== "none") {
        doc.text(`Status: ${node.data.status}`, margin, yPosition);
        yPosition += 5;
      }

      doc.text(
        `Complete: ${node.data.complete ? "Yes" : "No"}`,
        margin,
        yPosition,
      );
      yPosition += 5;

      if (node.data.priority) {
        doc.text(`Priority: ${node.data.priority}`, margin, yPosition);
        yPosition += 5;
      }

      if (node.data.flagged && node.data.flagged !== "none") {
        doc.text(`Flagged: ${node.data.flagged}`, margin, yPosition);
        yPosition += 5;
      }

      // Links
      if (node.data.links && node.data.links.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.text("Links:", margin, yPosition);
        yPosition += 5;
        doc.setFont("helvetica", "normal");
        node.data.links.forEach((link) => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(`  • ${link.title}: ${link.url}`, margin + 5, yPosition);
          yPosition += 5;
        });
      }

      // Values
      if (node.data.values && node.data.values.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.text("Values:", margin, yPosition);
        yPosition += 5;
        doc.setFont("helvetica", "normal");
        node.data.values.forEach((v) => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(`  • ${v.title}: ${v.value}`, margin + 5, yPosition);
          yPosition += 5;
        });
      }

      // Custom Properties
      if (node.data.customProperties && node.data.customProperties.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.text("Custom Properties:", margin, yPosition);
        yPosition += 5;
        doc.setFont("helvetica", "normal");
        node.data.customProperties.forEach((cp) => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(`  • ${cp.name}: ${cp.value}`, margin + 5, yPosition);
          yPosition += 5;
        });
      }

      // Add separator
      yPosition += 5;
      if (yPosition > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
    });

    // Save the PDF
    doc.save("map_data.pdf");

    return {
      success: true,
      description: "PDF exported successfully.",
    };
  } catch (error) {
    return {
      success: false,
      description: `There was an error exporting the data to PDF: ${
        (error as Error).message
      }`,
    } as ResponseType;
  }
};
